import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import './FeedbackPage.css';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const CATEGORIES = [
  { value: 'bug', icon: '🐛', label: 'Bug report', desc: "Something isn't working the way it should" },
  { value: 'issue', icon: '⚠️', label: 'Issue', desc: 'Something got in the way of using the tool' },
  { value: 'suggestion', icon: '💡', label: 'Suggestion', desc: 'An idea for something that would help' },
  { value: 'general', icon: '💬', label: 'General feedback', desc: 'Anything else you want us to know' },
];

export default function FeedbackPage({ onBack }) {
  const { getToken } = useAuth();
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    setError('');
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, message: message.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setStatus('sent');
      setMessage('');
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="fb-page">
      <div className="fb-hero">
        {onBack && (
          <button className="fb-back-link" onClick={onBack}>← Back</button>
        )}
        <h1 className="fb-hero-title">Feedback</h1>
        <p className="fb-hero-sub">
          Report an error, an unexpected result, or share an idea for what would help.
          Your feedback goes straight to the team and shapes what we build next.
        </p>
      </div>

      <div className="fb-body">
        {status === 'sent' ? (
          <div className="fb-sent">
            <div className="fb-sent-icon">✅</div>
            <h2>Thank you — feedback sent</h2>
            <p>The team has been notified. Feel free to send more, any time.</p>
            <button className="fb-btn-secondary" onClick={() => setStatus('idle')}>Send another</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="fb-form">
            <label className="fb-label">What kind of feedback is this?</label>
            <div className="fb-category-grid">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c.value}
                  className={`fb-category-card${category === c.value ? ' fb-category-card--active' : ''}`}
                  onClick={() => setCategory(c.value)}
                >
                  <span className="fb-category-icon">{c.icon}</span>
                  <span className="fb-category-label">{c.label}</span>
                  <span className="fb-category-desc">{c.desc}</span>
                </button>
              ))}
            </div>

            <label className="fb-label" htmlFor="fb-message">Tell us more</label>
            <textarea
              id="fb-message"
              className="fb-textarea"
              placeholder="What happened, what did you expect, or what would help?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              maxLength={5000}
              disabled={status === 'sending'}
              required
            />
            <div className="fb-char-count">{message.length}/5000</div>

            {status === 'error' && (
              <div className="fb-error">{error}</div>
            )}

            <button type="submit" className="fb-btn-primary" disabled={status === 'sending' || !message.trim()}>
              {status === 'sending' ? 'Sending…' : 'Send Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
